/**
 * T379 — StorageMigrationPanel (FR-054 / FR-055).
 *
 * Admin-only UI:
 *   - Progress: per-status attachment counts from `GET /admin/storage/status`.
 *   - SFTP credential form (inline per route contract; Phase-11 will move
 *     this to `tenant_settings`).
 *   - Start button → `POST /admin/storage/start`; the state machine is
 *     idempotent so a second click acts as "Resume" per FR-055.
 *   - Summary card rendered after each run + errors list.
 *   - Audit-stream visibility: reuses `AdminAuditLogViewer` filtered on
 *     the migration actions (T376).
 *
 * Phase-1 scope: connects to a single SFTP target. Per-tenant settings
 * + scheduler wiring are deferred.
 */
import { useState } from 'react';
import { Button, LoadingSpinner } from '../ui';
import { Input } from '../ui/Input';
import { useToastHelpers } from '../ui/Toast';
import {
  useStorageMigrationStatus,
  useStartStorageMigration,
  type StorageMigrationSummary,
} from '../../services/api/admin-storage';

const STATUS_ORDER = [
  'local',
  'copying',
  'remote_verified',
  'ref_switched',
  'local_deleted',
] as const;

export function StorageMigrationPanel() {
  const { data: status, isLoading } = useStorageMigrationStatus();
  const start = useStartStorageMigration();
  const toast = useToastHelpers();

  const [batchSize, setBatchSize] = useState('100');
  const [lastSummary, setLastSummary] = useState<StorageMigrationSummary | null>(null);

  if (isLoading) return <LoadingSpinner />;

  const total = Object.values(status?.byStatus ?? {}).reduce((s, n) => s + n, 0);
  const migrated = status?.byStatus?.['local_deleted'] ?? 0;
  const pct = total > 0 ? Math.round((migrated / total) * 100) : 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    // T462: SFTP target lives server-side now (env vars). Form only collects
    // batch-size; backend resolves the target + emits the audit event.
    try {
      const res = await start.mutateAsync({
        batchSize: batchSize ? Number(batchSize) : 100,
      });
      setLastSummary(res);
      toast.success(
        `Migration-Batch: ${res.newly_migrated} neu, ${res.already_migrated} übersprungen, ${res.failed} fehlgeschlagen.`
      );
    } catch (err) {
      toast.error(`Migration fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-6" data-testid="storage-migration-panel">
      <section className="rounded-lg border border-white/10 bg-white/5 p-5">
        <h3 className="text-lg text-white mb-3">Fortschritt</h3>
        <div className="flex items-baseline gap-3 mb-3">
          <span
            className="text-2xl text-white font-light"
            data-testid="storage-migration-percent"
          >
            {pct}%
          </span>
          <span className="text-sm text-white/50">
            {migrated}/{total} Anhänge auf Remote-Storage
          </span>
        </div>
        <div className="h-2 rounded bg-white/10 overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <dl className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
          {STATUS_ORDER.map((s) => (
            <div
              key={s}
              className="rounded border border-white/5 bg-black/30 px-3 py-2"
              data-testid={`storage-status-${s}`}
            >
              <dt className="text-white/50">{s}</dt>
              <dd className="text-white text-lg">{status?.byStatus?.[s] ?? 0}</dd>
            </div>
          ))}
        </dl>
      </section>

      <form
        onSubmit={submit}
        data-testid="storage-migration-form"
        className="rounded-lg border border-white/10 bg-white/5 p-5 space-y-3"
      >
        <h3 className="text-lg text-white">Migration starten</h3>
        <p className="text-xs text-white/50">
          SFTP-Ziel wird serverseitig konfiguriert (Umgebungsvariablen). Die
          State-Machine ist idempotent — wiederholtes Ausführen wirkt als
          „Resume" und überspringt bereits migrierte Anhänge (FR-055).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Batch-Größe"
            type="number"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            data-testid="sftp-batch-size"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            type="submit"
            disabled={start.isPending}
            data-testid="storage-migration-start"
          >
            {start.isPending
              ? 'Läuft…'
              : total > 0 && migrated < total
                ? 'Batch starten / fortsetzen'
                : 'Batch starten'}
          </Button>
        </div>
      </form>

      {lastSummary && (
        <section
          className="rounded-lg border border-white/10 bg-white/5 p-5"
          data-testid="storage-migration-summary"
        >
          <h3 className="text-lg text-white mb-2">Letzter Batch</h3>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            <SummaryStat label="Geprüft" value={lastSummary.considered} />
            <SummaryStat label="Neu migriert" value={lastSummary.newly_migrated} />
            <SummaryStat label="Bereits migriert" value={lastSummary.already_migrated} />
            <SummaryStat label="Fehler" value={lastSummary.failed} />
          </dl>
          {lastSummary.errors.length > 0 && (
            <ul className="mt-3 text-xs text-red-300 space-y-1">
              {lastSummary.errors.slice(0, 5).map((e) => (
                <li key={e.attachmentId}>
                  <code className="font-mono">{e.attachmentId.slice(0, 8)}</code>
                  {' — '}
                  {e.error}
                </li>
              ))}
              {lastSummary.errors.length > 5 && (
                <li className="text-white/50">
                  … {lastSummary.errors.length - 5} weitere
                </li>
              )}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-white/5 bg-black/30 px-3 py-2">
      <dt className="text-white/50">{label}</dt>
      <dd className="text-white text-lg">{value}</dd>
    </div>
  );
}
